// import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
// import { createPortal } from "react-dom";
// import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
// import moment from "moment";

// interface DateRangePickerProps {
//     startDate?: string;
//     endDate?: string;
//     onChange?: (startDate: string, endDate: string) => void;
//     placeholder?: string;
// }

// const DateRangePicker: React.FC<DateRangePickerProps> = ({
//     startDate = "",
//     endDate = "",
//     onChange,
//     placeholder = "Select Date Range",
// }) => {
//     const [isOpen, setIsOpen] = useState(false);
//     const containerRef = useRef<HTMLDivElement>(null);
//     const popupRef = useRef<HTMLDivElement>(null);
//     const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({
//         position: "fixed",
//         top: -9999,
//         left: -9999,
//         visibility: "hidden",
//     });

//     // Current month view state (left calendar base month)
//     const [viewDate, setViewDate] = useState(() => {
//         return startDate ? new Date(startDate) : new Date();
//     });

//     // Selection state (temporary until OK is clicked)
//     const [selectedStart, setSelectedStart] = useState<Date | null>(() => {
//         return startDate ? new Date(startDate) : null;
//     });
//     const [selectedEnd, setSelectedEnd] = useState<Date | null>(() => {
//         return endDate ? new Date(endDate) : null;
//     });

//     const [hoverDate, setHoverDate] = useState<Date | null>(null);

//     // Sync state with props
//     useEffect(() => {
//         setSelectedStart(startDate ? new Date(startDate) : null);
//         setSelectedEnd(endDate ? new Date(endDate) : null);
//         if (startDate) {
//             setViewDate(new Date(startDate));
//         }
//     }, [startDate, endDate]);

//     // Handle click outside to close dropdown. The popup is portaled to
//     // document.body, so it's no longer inside containerRef's subtree —
//     // clicks inside it must be checked separately via popupRef.
//     useEffect(() => {
//         const handleClickOutside = (event: MouseEvent) => {
//             const target = event.target as Node;
//             const insideTrigger = containerRef.current?.contains(target);
//             const insidePopup = popupRef.current?.contains(target);
//             if (!insideTrigger && !insidePopup) {
//                 setIsOpen(false);
//             }
//         };
//         document.addEventListener("mousedown", handleClickOutside);
//         return () => document.removeEventListener("mousedown", handleClickOutside);
//     }, []);

//     // Measure the trigger's position and clamp the popup so it never
//     // overflows any edge of the real viewport. Uses position: fixed
//     // coordinates directly (the popup is portaled to document.body, so
//     // it's unaffected by any ancestor's overflow/clipping).
//     useLayoutEffect(() => {
//         if (!isOpen || !containerRef.current || !popupRef.current) return;

//         const updatePosition = () => {
//             const trigger = containerRef.current!.getBoundingClientRect();
//             const popup = popupRef.current!.getBoundingClientRect();
//             const margin = 16;
//             const gap = 6;
//             const viewportWidth = window.innerWidth;
//             const viewportHeight = window.innerHeight;
//             const popupWidth = Math.min(560, viewportWidth - margin * 2);

//             // --- Horizontal clamp ---
//             let left = trigger.left;
//             const maxLeft = viewportWidth - margin - popupWidth;
//             if (left > maxLeft) left = maxLeft;
//             if (left < margin) left = margin;

//             // --- Vertical clamp: prefer opening below, flip above if that
//             // fits better, cap height with scroll as a last resort. ---
//             const spaceBelow = viewportHeight - trigger.bottom - margin - gap;
//             const spaceAbove = trigger.top - margin - gap;
//             const naturalHeight = popup.height;

//             const style: React.CSSProperties = {
//                 position: "fixed",
//                 left,
//                 width: popupWidth,
//                 overflowY: "auto",
//                 visibility: "visible",
//                 zIndex: 9999,
//             };

//             if (naturalHeight > spaceBelow && spaceAbove > spaceBelow) {
//                 style.bottom = viewportHeight - trigger.top + gap;
//                 style.top = "auto";
//                 style.maxHeight = Math.max(spaceAbove, 160);
//             } else {
//                 style.top = trigger.bottom + gap;
//                 style.bottom = "auto";
//                 style.maxHeight = Math.max(spaceBelow, 160);
//             }

//             setPopupStyle(style);
//         };

//         updatePosition();
//         window.addEventListener("resize", updatePosition);
//         window.addEventListener("scroll", updatePosition, true);
//         return () => {
//             window.removeEventListener("resize", updatePosition);
//             window.removeEventListener("scroll", updatePosition, true);
//         };
//     }, [isOpen]);

//     const leftMonth = viewDate.getMonth();
//     const leftYear = viewDate.getFullYear();

//     // Right calendar is always the next month
//     const rightDate = new Date(leftYear, leftMonth + 1, 1);
//     const rightMonth = rightDate.getMonth();
//     const rightYear = rightDate.getFullYear();

//     const navigateMonth = (direction: number) => {
//         setViewDate(new Date(leftYear, leftMonth + direction, 1));
//     };

//     // Helper to get all days of a month including prefix/suffix days
//     const getMonthDays = (year: number, month: number) => {
//         const firstDay = new Date(year, month, 1);
//         // Get day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
//         const firstDayIndex = firstDay.getDay();
//         // Convert so Monday is 0, Sunday is 6
//         const startOffset = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

//         const totalDays = new Date(year, month + 1, 0).getDate();
//         const days: { date: Date; isCurrentMonth: boolean }[] = [];

//         // Previous month days (trailing)
//         const prevMonthDate = new Date(year, month, 0);
//         const prevMonthTotalDays = prevMonthDate.getDate();
//         const prevMonthYear = prevMonthDate.getFullYear();
//         const prevMonthMonth = prevMonthDate.getMonth();
//         for (let i = startOffset - 1; i >= 0; i--) {
//             days.push({
//                 date: new Date(prevMonthYear, prevMonthMonth, prevMonthTotalDays - i),
//                 isCurrentMonth: false,
//             });
//         }

//         // Current month days
//         for (let i = 1; i <= totalDays; i++) {
//             days.push({
//                 date: new Date(year, month, i),
//                 isCurrentMonth: true,
//             });
//         }

//         // Next month days (leading) to complete 6-row grid (42 cells)
//         const remaining = 42 - days.length;
//         const nextMonthDate = new Date(year, month + 1, 1);
//         const nextMonthYear = nextMonthDate.getFullYear();
//         const nextMonthMonth = nextMonthDate.getMonth();
//         for (let i = 1; i <= remaining; i++) {
//             days.push({
//                 date: new Date(nextMonthYear, nextMonthMonth, i),
//                 isCurrentMonth: false,
//             });
//         }

//         return days;
//     };

//     const handleDayClick = (date: Date) => {
//         if (!selectedStart || (selectedStart && selectedEnd)) {
//             setSelectedStart(date);
//             setSelectedEnd(null);
//         } else {
//             if (date < selectedStart) {
//                 setSelectedStart(date);
//                 setSelectedEnd(null);
//             } else {
//                 setSelectedEnd(date);
//             }
//         }
//     };

//     const isDateSelected = (date: Date) => {
//         if (selectedStart && moment(date).isSame(selectedStart, "day")) return "selected-start";
//         if (selectedEnd && moment(date).isSame(selectedEnd, "day")) return "selected-end";
//         return null;
//     };

//     const isDateInRange = (date: Date) => {
//         if (selectedStart && selectedEnd) {
//             return date >= selectedStart && date <= selectedEnd;
//         }
//         if (selectedStart && hoverDate) {
//             return date >= selectedStart && date <= hoverDate;
//         }
//         return false;
//     };

//     const selectQuickRange = (rangeType: string) => {
//         const today = new Date();
//         today.setHours(0, 0, 0, 0);
//         let start = new Date();
//         let end = new Date();

//         switch (rangeType) {
//             case "today":
//                 start = today;
//                 end = today;
//                 break;
//             case "yesterday":
//                 const yesterday = new Date(today);
//                 yesterday.setDate(today.getDate() - 1);
//                 start = yesterday;
//                 end = yesterday;
//                 break;
//             case "last7":
//                 const last7 = new Date(today);
//                 last7.setDate(today.getDate() - 6);
//                 start = last7;
//                 end = today;
//                 break;
//             case "last30":
//                 const last30 = new Date(today);
//                 last30.setDate(today.getDate() - 29);
//                 start = last30;
//                 end = today;
//                 break;
//             case "thisMonth":
//                 start = new Date(today.getFullYear(), today.getMonth(), 1);
//                 end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
//                 break;
//             case "lastMonth":
//                 start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
//                 end = new Date(today.getFullYear(), today.getMonth(), 0);
//                 break;
//             default:
//                 return;
//         }

//         setSelectedStart(start);
//         setSelectedEnd(end);
//         setViewDate(start);
//     };

//     const handleApply = () => {
//         if (selectedStart && selectedEnd && onChange) {
//             onChange(
//                 moment(selectedStart).format("YYYY-MM-DD"),
//                 moment(selectedEnd).format("YYYY-MM-DD")
//             );
//         }
//         setIsOpen(false);
//     };

//     const formatInputLabel = () => {
//         if (selectedStart && selectedEnd) {
//             return `${moment(selectedStart).format("DD/MM/YYYY")} ~ ${moment(selectedEnd).format("DD/MM/YYYY")}`;
//         }
//         return placeholder;
//     };

//     const weekdayHeaders = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

//     const renderCalendarGrid = (year: number, month: number) => {
//         const days = getMonthDays(year, month);
//         const monthName = moment(new Date(year, month, 1)).format("MMM YYYY");

//         return (
//             <div className="flex-1 px-3 min-w-[220px]">
//                 {/* Header */}
//                 <div className="flex items-center justify-between h-8 mb-2">
//                     <span className="text-xs font-semibold text-slate-800 tracking-wide capitalize">
//                         {monthName}
//                     </span>
//                 </div>

//                 {/* Weekdays */}
//                 <div className="grid grid-cols-7 gap-y-0.5 mb-1 text-center">
//                     {weekdayHeaders.map((day) => (
//                         <div key={day} className="text-[10px] font-semibold text-slate-400 py-0.5">
//                             {day}
//                         </div>
//                     ))}
//                 </div>

//                 {/* Days Grid */}
//                 <div className="grid grid-cols-7 gap-y-0.5 text-center">
//                     {days.map((item, index) => {
//                         const dateType = isDateSelected(item.date);
//                         const inRange = isDateInRange(item.date);
//                         const isStart = dateType === "selected-start";
//                         const isEnd = dateType === "selected-end";

//                         return (
//                             <button
//                                 key={index}
//                                 type="button"
//                                 onClick={() => handleDayClick(item.date)}
//                                 onMouseEnter={() => !selectedEnd && setHoverDate(item.date)}
//                                 className={`
//                                     h-7 w-full flex items-center justify-center text-[11px] font-semibold rounded-md transition-all relative cursor-pointer
//                                     ${item.isCurrentMonth ? "text-slate-700" : "text-slate-300"}
//                                     ${inRange && !isStart && !isEnd ? "bg-blue-50 text-blue-600 rounded-none" : ""}
//                                     ${isStart ? "bg-blue-600 text-white rounded-l-md z-10" : ""}
//                                     ${isEnd ? "bg-blue-600 text-white rounded-r-md z-10" : ""}
//                                     ${isStart && !selectedEnd ? "rounded-md" : ""}
//                                     hover:bg-slate-100 hover:text-slate-900
//                                     ${(isStart || isEnd) ? "hover:bg-blue-700 hover:text-white" : ""}
//                                 `}
//                             >
//                                 {item.date.getDate()}
//                             </button>
//                         );
//                     })}
//                 </div>
//             </div>
//         );
//     };

//     const popupContent = isOpen && (
//         <div
//             ref={popupRef}
//             style={popupStyle}
//             className="bg-white border border-slate-200/80 rounded-xl shadow-xl p-3 max-w-[calc(100vw-2rem)] flex flex-col gap-3 animate-in fade-in duration-150"
//         >

//                     {/* Navigation Row */}
//                     <div className="flex items-center justify-between pb-2 border-b border-slate-100">
//                         <button
//                             type="button"
//                             onClick={() => navigateMonth(-1)}
//                             className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 transition-all cursor-pointer"
//                         >
//                             <ChevronLeft size={14} className="text-slate-600" />
//                         </button>
//                         <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded">
//                             Select Dates Range
//                         </span>
//                         <button
//                             type="button"
//                             onClick={() => navigateMonth(1)}
//                             className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 transition-all cursor-pointer"
//                         >
//                             <ChevronRight size={14} className="text-slate-600" />
//                         </button>
//                     </div>

//                     {/* Dual Calendars */}
//                     <div className="flex flex-col sm:flex-row sm:divide-x divide-slate-100 overflow-x-auto">
//                         {renderCalendarGrid(leftYear, leftMonth)}
//                         {renderCalendarGrid(rightYear, rightMonth)}
//                     </div>

//                     {/* Footer Controls */}
//                     <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100 mt-1">
//                         {/* Quick Filters */}
//                         <div className="flex flex-wrap gap-1">
//                             <button
//                                 type="button"
//                                 onClick={() => selectQuickRange("today")}
//                                 className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
//                             >
//                                 Today
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={() => selectQuickRange("yesterday")}
//                                 className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
//                             >
//                                 Yesterday
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={() => selectQuickRange("last7")}
//                                 className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
//                             >
//                                 Last 7 Days
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={() => selectQuickRange("last30")}
//                                 className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
//                             >
//                                 Last 30 Days
//                             </button>
//                         </div>

//                         {/* Apply & Cancel */}
//                         <div className="flex items-center gap-2">
//                             <button
//                                 type="button"
//                                 onClick={() => setIsOpen(false)}
//                                 className="px-3 py-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={handleApply}
//                                 disabled={!selectedStart || !selectedEnd}
//                                 className="px-4 py-1.5 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 rounded-lg transition-all shadow-sm shadow-blue-500/10 cursor-pointer disabled:cursor-not-allowed"
//                             >
//                                 OK
//                             </button>
//                         </div>
//                     </div>
//         </div>
//     );

//     return (
//         <div ref={containerRef} className="relative inline-block w-full">
//             {/* Input Trigger */}
//             <button
//                 type="button"
//                 onClick={() => setIsOpen(!isOpen)}
//                 className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:border-primary/50 transition-colors shadow-xs text-left cursor-pointer"
//             >
//                 <div className="flex items-center gap-2 text-slate-700">
//                     <CalendarIcon size={14} className="text-slate-400" />
//                     <span className="font-medium truncate">{formatInputLabel()}</span>
//                 </div>
//             </button>

//             {/* Dropdown is portaled to document.body so it can never be
//                 clipped by an ancestor's overflow/clipping context. */}
//             {popupContent && createPortal(popupContent, document.body)}
//         </div>
//     );
// };

// export default DateRangePicker;


import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import moment from "moment";

interface DateRangePickerProps {
    startDate?: string;
    endDate?: string;
    onChange?: (startDate: string, endDate: string) => void;
    placeholder?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate = "",
    endDate = "",
    onChange,
    placeholder = "Select Date Range",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({
        position: "fixed",
        top: -9999,
        left: -9999,
        visibility: "hidden",
    });

    // Current month view state (left calendar base month)
    const [viewDate, setViewDate] = useState(() => {
        return startDate ? new Date(startDate) : new Date();
    });

    // Selection state (temporary until OK is clicked)
    const [selectedStart, setSelectedStart] = useState<Date | null>(() => {
        return startDate ? new Date(startDate) : null;
    });
    const [selectedEnd, setSelectedEnd] = useState<Date | null>(() => {
        return endDate ? new Date(endDate) : null;
    });

    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    // Sync state with props
    useEffect(() => {
        setSelectedStart(startDate ? new Date(startDate) : null);
        setSelectedEnd(endDate ? new Date(endDate) : null);
        if (startDate) {
            setViewDate(new Date(startDate));
        }
    }, [startDate, endDate]);

    // Handle click outside to close dropdown. The popup is portaled to
    // document.body, so it's no longer inside containerRef's subtree —
    // clicks inside it must be checked separately via popupRef.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideTrigger = containerRef.current?.contains(target);
            const insidePopup = popupRef.current?.contains(target);
            if (!insideTrigger && !insidePopup) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Measure the trigger's position and clamp the popup so it never
    // overflows any edge of the real viewport. Uses position: fixed
    // coordinates directly (the popup is portaled to document.body, so
    // it's unaffected by any ancestor's overflow/clipping).
    useLayoutEffect(() => {
        if (!isOpen || !containerRef.current || !popupRef.current) return;

        const updatePosition = () => {
            const trigger = containerRef.current!.getBoundingClientRect();
            const popup = popupRef.current!.getBoundingClientRect();
            const margin = 16;
            const gap = 6;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const popupWidth = Math.min(560, viewportWidth - margin * 2);

            // --- Horizontal clamp ---
            let left = trigger.left;
            const maxLeft = viewportWidth - margin - popupWidth;
            if (left > maxLeft) left = maxLeft;
            if (left < margin) left = margin;

            // --- Vertical clamp: prefer opening below, flip above if that
            // fits better, cap height with scroll as a last resort. ---
            const spaceBelow = viewportHeight - trigger.bottom - margin - gap;
            const spaceAbove = trigger.top - margin - gap;
            const naturalHeight = popup.height;

            const style: React.CSSProperties = {
                position: "fixed",
                left,
                width: popupWidth,
                overflowY: "auto",
                visibility: "visible",
                zIndex: 9999,
            };

            if (naturalHeight > spaceBelow && spaceAbove > spaceBelow) {
                style.bottom = viewportHeight - trigger.top + gap;
                style.top = "auto";
                style.maxHeight = Math.max(spaceAbove, 160);
            } else {
                style.top = trigger.bottom + gap;
                style.bottom = "auto";
                style.maxHeight = Math.max(spaceBelow, 160);
            }

            setPopupStyle(style);
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen]);

    const leftMonth = viewDate.getMonth();
    const leftYear = viewDate.getFullYear();

    // Right calendar is always the next month
    const rightDate = new Date(leftYear, leftMonth + 1, 1);
    const rightMonth = rightDate.getMonth();
    const rightYear = rightDate.getFullYear();

    const navigateMonth = (direction: number) => {
        setViewDate(new Date(leftYear, leftMonth + direction, 1));
    };

    // Helper to get all days of a month including prefix/suffix days
    const getMonthDays = (year: number, month: number) => {
        const firstDay = new Date(year, month, 1);
        // Get day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
        const firstDayIndex = firstDay.getDay();
        // Convert so Monday is 0, Sunday is 6
        const startOffset = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

        const totalDays = new Date(year, month + 1, 0).getDate();
        const days: { date: Date; isCurrentMonth: boolean }[] = [];

        // Previous month days (trailing)
        const prevMonthDate = new Date(year, month, 0);
        const prevMonthTotalDays = prevMonthDate.getDate();
        const prevMonthYear = prevMonthDate.getFullYear();
        const prevMonthMonth = prevMonthDate.getMonth();
        for (let i = startOffset - 1; i >= 0; i--) {
            days.push({
                date: new Date(prevMonthYear, prevMonthMonth, prevMonthTotalDays - i),
                isCurrentMonth: false,
            });
        }

        // Current month days
        for (let i = 1; i <= totalDays; i++) {
            days.push({
                date: new Date(year, month, i),
                isCurrentMonth: true,
            });
        }

        // Next month days (leading) to complete 6-row grid (42 cells)
        const remaining = 42 - days.length;
        const nextMonthDate = new Date(year, month + 1, 1);
        const nextMonthYear = nextMonthDate.getFullYear();
        const nextMonthMonth = nextMonthDate.getMonth();
        for (let i = 1; i <= remaining; i++) {
            days.push({
                date: new Date(nextMonthYear, nextMonthMonth, i),
                isCurrentMonth: false,
            });
        }

        return days;
    };

    const handleDayClick = (date: Date) => {
        if (!selectedStart || (selectedStart && selectedEnd)) {
            setSelectedStart(date);
            setSelectedEnd(null);
        } else {
            if (date < selectedStart) {
                setSelectedStart(date);
                setSelectedEnd(null);
            } else {
                setSelectedEnd(date);
            }
        }
    };

    const isDateSelected = (date: Date) => {
        if (selectedStart && moment(date).isSame(selectedStart, "day")) return "selected-start";
        if (selectedEnd && moment(date).isSame(selectedEnd, "day")) return "selected-end";
        return null;
    };

    const isDateInRange = (date: Date) => {
        if (selectedStart && selectedEnd) {
            return date >= selectedStart && date <= selectedEnd;
        }
        if (selectedStart && hoverDate) {
            return date >= selectedStart && date <= hoverDate;
        }
        return false;
    };

    const selectQuickRange = (rangeType: string) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let start = new Date();
        let end = new Date();

        switch (rangeType) {
            case "today":
                start = today;
                end = today;
                break;
            case "yesterday":
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                start = yesterday;
                end = yesterday;
                break;
            case "last7":
                const last7 = new Date(today);
                last7.setDate(today.getDate() - 6);
                start = last7;
                end = today;
                break;
            case "last30":
                const last30 = new Date(today);
                last30.setDate(today.getDate() - 29);
                start = last30;
                end = today;
                break;
            case "thisMonth":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case "lastMonth":
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "10days":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth(), 10);
                break;
            case "15days":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth(), 15);
                break;
            case "20days":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth(), 20);
                break;
            default:
                return;
        }

        setSelectedStart(start);
        setSelectedEnd(end);
        setViewDate(start);
    };

    const handleApply = () => {
        if (selectedStart && selectedEnd && onChange) {
            onChange(
                moment(selectedStart).format("YYYY-MM-DD"),
                moment(selectedEnd).format("YYYY-MM-DD")
            );
        }
        setIsOpen(false);
    };

    const formatInputLabel = () => {
        if (selectedStart && selectedEnd) {
            return `${moment(selectedStart).format("DD/MM/YYYY")} ~ ${moment(selectedEnd).format("DD/MM/YYYY")}`;
        }
        return placeholder;
    };

    const weekdayHeaders = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

    const renderCalendarGrid = (year: number, month: number) => {
        const days = getMonthDays(year, month);
        const monthName = moment(new Date(year, month, 1)).format("MMM YYYY");

        return (
            <div className="flex-1 px-3 min-w-[220px]">
                {/* Header */}
                <div className="flex items-center justify-between h-8 mb-2">
                    <span className="text-xs font-semibold text-slate-800 tracking-wide capitalize">
                        {monthName}
                    </span>
                </div>

                {/* Weekdays */}
                <div className="grid grid-cols-7 gap-y-0.5 mb-1 text-center">
                    {weekdayHeaders.map((day) => (
                        <div key={day} className="text-[10px] font-semibold text-slate-400 py-0.5">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-y-0.5 text-center">
                    {days.map((item, index) => {
                        const dateType = isDateSelected(item.date);
                        const inRange = isDateInRange(item.date);
                        const isStart = dateType === "selected-start";
                        const isEnd = dateType === "selected-end";

                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={() => handleDayClick(item.date)}
                                onMouseEnter={() => !selectedEnd && setHoverDate(item.date)}
                                className={`
                                    h-7 w-full flex items-center justify-center text-[11px] font-semibold rounded-md transition-all relative cursor-pointer
                                    ${item.isCurrentMonth ? "text-slate-700" : "text-slate-300"}
                                    ${inRange && !isStart && !isEnd ? "bg-blue-50 text-blue-600 rounded-none" : ""}
                                    ${isStart ? "bg-blue-600 text-white rounded-l-md z-10" : ""}
                                    ${isEnd ? "bg-blue-600 text-white rounded-r-md z-10" : ""}
                                    ${isStart && !selectedEnd ? "rounded-md" : ""}
                                    hover:bg-slate-100 hover:text-slate-900
                                    ${(isStart || isEnd) ? "hover:bg-blue-700 hover:text-white" : ""}
                                `}
                            >
                                {item.date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const popupContent = isOpen && (
        <div
            ref={popupRef}
            style={popupStyle}
            className="bg-white border border-slate-200/80 rounded-xl shadow-xl p-3 max-w-[calc(100vw-2rem)] flex flex-col gap-3 animate-in fade-in duration-150"
        >

                    {/* Navigation Row */}
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                        <button
                            type="button"
                            onClick={() => navigateMonth(-1)}
                            className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 transition-all cursor-pointer"
                        >
                            <ChevronLeft size={14} className="text-slate-600" />
                        </button>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded">
                            Select Dates Range
                        </span>
                        <button
                            type="button"
                            onClick={() => navigateMonth(1)}
                            className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 transition-all cursor-pointer"
                        >
                            <ChevronRight size={14} className="text-slate-600" />
                        </button>
                    </div>

                    {/* Dual Calendars */}
                    <div className="flex flex-col sm:flex-row sm:divide-x divide-slate-100 overflow-x-auto">
                        {renderCalendarGrid(leftYear, leftMonth)}
                        {renderCalendarGrid(rightYear, rightMonth)}
                    </div>

                    {/* Footer Controls */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100 mt-1">
                        {/* Quick Filters */}
                        <div className="flex flex-wrap gap-1">
                            <button
                                type="button"
                                onClick={() => selectQuickRange("today")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                Today
                            </button>
                            {/* <button
                                type="button"
                                onClick={() => selectQuickRange("yesterday")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                Yesterday
                            </button> */}
                            {/* <button
                                type="button"
                                onClick={() => selectQuickRange("last7")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                Last 7 Days
                            </button> */}
                            <button
                                type="button"
                                onClick={() => selectQuickRange("last30")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                Last 30 Days
                            </button>
                            <button
                                type="button"
                                onClick={() => selectQuickRange("10days")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                10 Days
                            </button>
                            <button
                                type="button"
                                onClick={() => selectQuickRange("15days")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                15 Days
                            </button>
                            <button
                                type="button"
                                onClick={() => selectQuickRange("20days")}
                                className="text-[11px] font-bold px-2 py-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                            >
                                20 Days
                            </button>
                        </div>

                        {/* Apply & Cancel */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="px-3 py-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleApply}
                                disabled={!selectedStart || !selectedEnd}
                                className="px-4 py-1.5 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 rounded-lg transition-all shadow-sm shadow-blue-500/10 cursor-pointer disabled:cursor-not-allowed"
                            >
                                OK
                            </button>
                        </div>
                    </div>
        </div>
    );

    return (
        <div ref={containerRef} className="relative inline-block w-full">
            {/* Input Trigger */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:border-primary/50 transition-colors shadow-xs text-left cursor-pointer"
            >
                <div className="flex items-center gap-2 text-slate-700">
                    <CalendarIcon size={14} className="text-slate-400" />
                    <span className="font-medium truncate">{formatInputLabel()}</span>
                </div>
            </button>

            {/* Dropdown is portaled to document.body so it can never be
                clipped by an ancestor's overflow/clipping context. */}
            {popupContent && createPortal(popupContent, document.body)}
        </div>
    );
};

export default DateRangePicker;